import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import {
    Button,
    Card,
    Text,
    Heading,
    Container
} from '../components/ui/index.jsx';

export default function About() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1">
                    <Container size="4xl">
                        {/* Hero Section */}
                        <div className="py-16 md:py-24 text-center px-4">
                            <Heading level={1} className="mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
                                About The Architect
                            </Heading>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF]"></div>
                        </div>

                        {/* Content */}
                        <div className="max-w-none px-4 md:px-0 space-y-12">

                            {/* Our Story Section */}
                            <div className="glass-panel p-8 md:p-12 rounded-2xl border-white/10">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="w-10 h-10 rounded-lg bg-[#7B61FF]/20 flex items-center justify-center">
                                        <svg className="w-5 h-5 text-[#7B61FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                    <Heading level={2} className="text-white">Our Story</Heading>
                                </div>

                                <Text variant="primary" size="lg" className="mb-6 leading-relaxed text-gray-200">
                                    The Architect was founded by two industry veterans with over 20 years of experience each in building and scaling digital products.
                                </Text>

                                <Text variant="primary" size="lg" className="mb-6 leading-relaxed text-gray-200">
                                    A seasoned Solution Architect who designed complex, scalable systems for startups and enterprises. And a Deputy CEO of a leading technology company, with a strong track record in strategy and growth.
                                </Text>

                                <Text variant="primary" size="lg" className="leading-relaxed text-gray-200">
                                    Together, we've experienced firsthand how difficult and fragmented the process of translating an idea into robust architecture can be — especially for teams without deep technical expertise.
                                </Text>
                            </div>

                            {/* Our Vision Section */}
                            <div className="glass-panel p-8 md:p-12 rounded-2xl border-white/10">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="w-10 h-10 rounded-lg bg-[#7B61FF]/20 flex items-center justify-center">
                                        <svg className="w-5 h-5 text-[#7B61FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <Heading level={2} className="text-white">Our Vision</Heading>
                                </div>

                                <Text variant="primary" size="lg" className="mb-6 leading-relaxed text-gray-200">
                                    We believe the future of software development is <span className="text-[#BA87FF] font-semibold">AI-assisted, not AI-dependent</span>.
                                </Text>

                                <Text variant="primary" size="lg" className="mb-6 leading-relaxed text-gray-200">
                                    Most platforms promise to build your app — but trap you in their ecosystem. You don't own the code. You don't control the infrastructure. You're renting, not building.
                                </Text>

                                <div className="border-l-4 border-[#7B61FF] p-6 my-8 bg-[#7B61FF]/10 rounded-r-lg">
                                    <Text variant="accent" size="xl" weight="semibold" className="text-white">
                                        We're different.
                                    </Text>
                                </div>

                                <Text variant="primary" size="lg" className="mb-6 leading-relaxed text-gray-200">
                                    The Architect takes your idea and transforms it into production-ready architecture — complete with data models, tech stack recommendations, and development prompts. Then guides you to deploy a <span className="text-white font-semibold">real application</span> — hosted on <span className="text-white font-semibold">your own infrastructure</span>, with <span className="text-white font-semibold">100% ownership of the code</span>.
                                </Text>

                                {/* Key Differentiators */}
                                <div className="grid md:grid-cols-3 gap-4 my-8">
                                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 text-center">
                                        <div className="w-12 h-12 rounded-full bg-[#7B61FF]/20 flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-6 h-6 text-[#7B61FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                            </svg>
                                        </div>
                                        <Text weight="semibold" className="text-white mb-1">Your Code</Text>
                                        <Text size="sm" variant="secondary">No black boxes</Text>
                                    </div>
                                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 text-center">
                                        <div className="w-12 h-12 rounded-full bg-[#7B61FF]/20 flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-6 h-6 text-[#7B61FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                                            </svg>
                                        </div>
                                        <Text weight="semibold" className="text-white mb-1">Your Infrastructure</Text>
                                        <Text size="sm" variant="secondary">No vendor lock-in</Text>
                                    </div>
                                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 text-center">
                                        <div className="w-12 h-12 rounded-full bg-[#7B61FF]/20 flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-6 h-6 text-[#7B61FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                        </div>
                                        <Text weight="semibold" className="text-white mb-1">Your Future</Text>
                                        <Text size="sm" variant="secondary">No limits</Text>
                                    </div>
                                </div>

                                <div className="border-l-4 border-[#7B61FF] p-6 mt-8 bg-[#7B61FF]/10 rounded-r-lg">
                                    <Text variant="accent" size="lg" weight="medium" className="text-[#BA87FF] italic">
                                        "We're not just building a tool — we're redefining the role of architects in a world where AI is your co-designer."
                                    </Text>
                                </div>
                            </div>

                            {/* Tagline */}
                            <div className="text-center py-8">
                                <Text size="2xl" weight="bold" className="bg-clip-text text-transparent bg-gradient-to-r from-[#7B61FF] to-[#BA87FF]">
                                    From idea to app. Fully yours.
                                </Text>
                            </div>

                        </div>
                    </Container>
                </main>

                <Footer />
            </div>
        </div>
    );
} 